import { FC, useCallback, useEffect, useState } from 'react'

import { createPortalSession, getLrsConfig, getSessionById } from 'components/contentspace/api'
import { usePublicPortalCourse } from 'components/contentspace/hooks'
import { ScormSessionProvider } from 'components/contentspace/scormInstance/ScormSessionContext'
import {
  clearPortalSession,
  getPortalAuthCookie,
  getSessionCookie,
  identityKeyFromAuth,
  setPortalAuthCookie,
  setSessionCookie,
  type IIdentityKey,
} from 'components/contentspace/sessionCookie'
import type { IContentSpaceSession } from 'components/contentspace/types'
import Loader from 'components/uiKit/Loader'
import { isPortalMode, portalBasename } from 'routes/history'

import { usePortalBasePath, usePortalParams } from '../../hooks/usePortalParams'
import { PortalLmsPlayer } from './PortalLmsPlayer'
import { SessionResume } from './components/SessionResume/SessionResume'

export const PortalLms: FC = () => {
  const { companyId, portalId, courseId } = usePortalParams()
  const { data: course, loading, error } = usePublicPortalCourse(companyId!, portalId!, courseId!)

  const [session, setSession] = useState<IContentSpaceSession | null>(null)
  const [existingSession, setExistingSession] = useState<IContentSpaceSession | null>(null)
  const [checkingSession, setCheckingSession] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)

  const basePath = usePortalBasePath()
  const portalLandingPath = basePath || '/'
  const portalRedirectUrl = isPortalMode
    ? basePath || portalBasename || '/'
    : `/#${basePath || '/'}`

  const isStartCourse = course?.coursePortal?.startCourseId === courseId
  const effectiveResumeMode = isStartCourse
    ? 'ALWAYS_RESUME'
    : (course?.coursePortal?.sessionResumeMode ?? 'ASK')
  const effectiveHideHeader = isStartCourse ? true : (course?.coursePortal?.hideHeader ?? false)

  const ensureIdentity = useCallback((): IIdentityKey => {
    const auth = getPortalAuthCookie(portalId!) ?? { verified: false }
    const existing = identityKeyFromAuth(auth)
    if (existing) {
      return existing
    }
    // Bootstrap анонимной identity, чтобы кука сессии не делилась с другим юзером.
    const anonSessionId = crypto.randomUUID()
    setPortalAuthCookie(portalId!, { ...auth, anonSessionId })
    return { kind: 'anon', short: anonSessionId }
  }, [portalId])

  const createNewSession = useCallback(
    async (opts?: { forceNew?: boolean }) => {
      const portalAuth = getPortalAuthCookie(portalId!)
      const type = portalAuth?.verified ? 'EMAIL' : portalAuth?.email ? 'EMAIL' : 'ANONYMOUS'

      try {
        const { session: newSession, sessionToken } = await createPortalSession(
          companyId!,
          portalId!,
          courseId!,
          {
            type,
            email: portalAuth?.email,
            userName: portalAuth?.name,
            forceNew: opts?.forceNew,
          },
        )
        const sessionWithToken = { ...newSession, sessionToken }
        const identity = ensureIdentity()
        setSessionCookie(portalId!, courseId!, identity, {
          sessionId: newSession.id,
          sessionToken,
        })
        setSession(sessionWithToken)
        getLrsConfig(newSession.id).catch(() => {
          // LRS not configured, ignore
        })
      } catch (err: unknown) {
        const error = err as Record<string, Record<string, number> | string | number>
        const status = (error?.response as Record<string, number>)?.status ?? error?.status
        if (status === 403 || status === 401) {
          clearPortalSession(portalId!)
          window.location.href = portalRedirectUrl
          return
        }
        setSessionError((error?.message as string) || 'Failed to create session')
      }
    },
    [companyId, portalId, courseId, portalRedirectUrl, ensureIdentity],
  )

  useEffect(() => {
    if (!companyId || !portalId || !courseId) {
      return
    }

    // Identity-keyed cookie lookup. If identity не определилась — нет смысла
    // искать куку: либо это первый заход (анона ещё не bootstrap-нули), либо
    // юзер просто уйдёт по createNewSession-ветке. Идентичность будет создана
    // там же.
    const portalAuth = getPortalAuthCookie(portalId)
    const identity = identityKeyFromAuth(portalAuth)
    const cookie = identity ? getSessionCookie(portalId, courseId, identity) : null
    if (cookie) {
      getSessionById(cookie.sessionId, cookie.sessionToken)
        .then(({ session: existingSess }) => {
          if (existingSess) {
            setExistingSession({ ...existingSess, sessionToken: cookie.sessionToken ?? null })
          }
        })
        .catch(() => {
          // Cookie invalid, will create new session
        })
        .finally(() => {
          setCheckingSession(false)
        })
    } else {
      setCheckingSession(false)
    }
  }, [companyId, portalId, courseId])

  useEffect(() => {
    if (checkingSession || session || !course) {
      return
    }

    if (existingSession) {
      if (effectiveResumeMode === 'ALWAYS_RESUME') {
        setSession(existingSession)
        setExistingSession(null)
      } else if (effectiveResumeMode === 'ALWAYS_RESET') {
        setExistingSession(null)
        createNewSession()
      }
      // ASK mode: do nothing, SessionResume component will handle it
      return
    }

    createNewSession()
  }, [checkingSession, existingSession, session, course, createNewSession, effectiveResumeMode])

  if (loading || checkingSession) {
    return <Loader name='publicCourseLoader' />
  }
  if (error || !course) {
    return <div>Error loading course: {error?.message || 'Not found'}</div>
  }
  if (sessionError) {
    return <div>Error creating session: {sessionError}</div>
  }

  if (existingSession && !session && effectiveResumeMode === 'ASK') {
    return (
      <SessionResume
        courseName={course.name}
        onContinue={() => {
          setSession(existingSession)
        }}
        onStartNew={() => {
          setExistingSession(null)
          createNewSession({ forceNew: true })
        }}
        portalLandingUrl={portalLandingPath}
        portalRedirectUrl={portalRedirectUrl}
        session={existingSession}
      />
    )
  }

  if (!session || !course.url) {
    return <Loader name='creatingSession' />
  }

  return (
    <ScormSessionProvider initialSession={session}>
      <PortalLmsPlayer
        courseName={course.name}
        courseUrl={course.url}
        hideHeader={effectiveHideHeader}
        portalLandingPath={portalLandingPath}
        portalRedirectUrl={portalRedirectUrl}
        queryLoading={false}
        sessionCreating={false}
        sessionReady
      />
    </ScormSessionProvider>
  )
}
